import axios from 'axios'
import { baseAPICall } from './baseAPICall'
import { API_HOST, clientInstance } from '../utils/http-client'
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { invalidateProductQueries } from '../utils/productQueryInvalidation'
import {
  PRODUCTS_CATALOG_KEY,
  VARIANTS_CATALOG_KEY,
} from '../constants/tableQueryKeys'
import type { Product, Variant, DistributorCode } from '../models/product'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * products_api.ts
 *
 * Centralized API layer for product and variant catalog operations.
 * Follows the pattern established in tradespend_api.ts.
 *
 * Key features:
 * - Feature-flag-aware endpoint switching
 * - Automatic query invalidation on mutations
 * - Type-safe request/response handling
 * - React Query hooks for all operations
 */

// ============================================================================
// Types & Interfaces
// ============================================================================

/**
 * Extended Variant type from the catalog API.
 * Includes additional fields beyond the base Variant model.
 */
export interface VariantCatalog {
  id: number
  name: string
  sku: string
  gtin: string | null
  product_id: number | null
  product_name: string | null
  match_status: 'matched' | 'unmatched' | 'ignored'
  units_per_case: number | null
  case_weight: number | null
  case_weight_unit: string | null
  unit_weight: number | null
  unit_weight_unit: string | null
  format_pack_size: string | null
  avg_price: number | null
  distributor_codes_count: number
  distributor_codes: DistributorCode[]
  brand_id: number | null
  brand_name: string | null
  syndigo_product?: SyndigoProduct | null
  created: string
  modified: string
  company?: number
}

export type PackageHierarchyLevel =
  | 'BASE_UNIT_OR_EACH'
  | 'INNER_PACK'
  | 'CASE'
  | 'PALLET'
  | 'MIXED_MODULE'

export interface SyndigoProduct {
  syndigo_product_id: string
  package_hierarchy_level: PackageHierarchyLevel
  brand_name: string | null
  category: string | null
  package_type: string | null
  last_modified_at_syndigo: string | null
  last_synced_at: string
}

export interface VariantsCatalogResponse {
  count: number
  unmatched_count: number
  page: number
  page_size: number
  results: VariantCatalog[]
}

export interface VariantsCatalogFilters {
  search?: string
  match_status?: 'matched' | 'unmatched' | 'all'
  product?: number[]
  sku?: string
  gtin?: string
}

export interface ProductsCatalogResponse {
  count: number
  next: string | null
  previous: string | null
  results: Product[]
}

export interface ProductsCatalogParams {
  page?: number
  limit?: number
  page_size?: number
  sort?: string
  ordering?: string
  search?: string
  [key: string]: any
}

export interface VariantDetail extends Variant {
  id: number
  product_id: number | null
  product_name: string | null
  match_status: 'matched' | 'unmatched' | 'ignored'
  distributor_codes_count: number
  created: string
  modified: string
}

export interface BulkMatchRequest {
  // Mode 1: Explicit variant IDs
  matches?: Array<{
    variant_id: number
    product_id: number | null
  }>
  // Mode 2: Filter-based link (for "select all" to link)
  product_id?: number
  filters?: Record<string, any>
  exclude_ids?: number[]
  // Mode 3: Filter-based unlink (for "select all" to unlink)
  unlink_mode?: boolean
  // Internal field for optimistic UI (not sent to server)
  _productName?: string
}

export interface BulkMatchResponse {
  matched_count: number
  failed_count: number
  errors: Array<{ variant_id?: number; error: string }>
  async: boolean
  message?: string
}

export interface ExportProductsRequest {
  selected_columns: string[]
  export_type: 'csv' | 'xlsx'
  include_ids?: number[]
  exclude_ids?: number[]
}

// Re-export types from models
export type { Product, Variant, DistributorCode } from '../models/product'

// ============================================================================
// Products Catalog API
// ============================================================================

/**
 * Fetch products from catalog.
 * Always uses the new catalog endpoint.
 */
export async function getProductsCatalog(params: ProductsCatalogParams = {}) {
  const endpoint = 'products/catalog/products/'

  return baseAPICall<ProductsCatalogResponse>(() =>
    clientInstance.get(endpoint, { params })
  )
}

export interface UseGetProductsCatalogOptions {
  params?: ProductsCatalogParams
  enabled?: boolean
  refetchOnMount?: boolean | 'always'
  /**
   * Optional custom query key for advanced use cases (e.g., data tables).
   * If provided, this will be used instead of the default query key.
   * Useful when you need specific cache invalidation behavior.
   */
  customQueryKey?: any[]
}

/**
 * Hook for fetching products catalog.
 * Always uses the new catalog endpoint.
 *
 * @param options - Configuration options
 * @param options.params - Query parameters (page, limit, sort, filters, etc.)
 * @param options.enabled - Whether query is enabled (default: true)
 * @param options.refetchOnMount - Refetch behavior on component mount
 * @param options.customQueryKey - Custom query key for advanced caching needs
 *
 * @example
 * // Standard usage
 * const { data } = useGetProductsCatalog({ params: { page: 1, limit: 50 } })
 *
 * @example
 * // With custom query key (for tables)
 * const customKey = getTableQueryKey({ tableKey: 'products', filterParams, page })
 * const { data } = useGetProductsCatalog({
 *   params: { ...filters, page },
 *   customQueryKey: customKey
 * })
 */
export const useGetProductsCatalog = (
  options: UseGetProductsCatalogOptions | ProductsCatalogParams = {}
) => {
  // Support legacy call signature: useGetProductsCatalog(params, useNewCatalog, enabled)
  // OR new options object: useGetProductsCatalog({ params, useNewCatalog, enabled, ... })
  const {
    params = {},
    enabled = true,
    refetchOnMount,
    customQueryKey,
  } = 'params' in options ? options : { params: options }

  // Use custom query key if provided, otherwise use standard key
  const queryKey = customQueryKey ?? [PRODUCTS_CATALOG_KEY, params]

  return useQuery({
    queryKey,
    queryFn: () => getProductsCatalog(params),
    enabled,
    refetchOnMount,
  })
}

/**
 * Export products catalog to CSV/XLSX.
 * Returns a blob for download.
 */
export async function exportProductsCatalog(
  filters: Record<string, any>,
  request: ExportProductsRequest
) {
  return clientInstance.post('products/catalog/products/export/', request, {
    params: filters,
    responseType: 'blob',
  })
}

/**
 * Export product variants catalog to CSV/XLSX.
 * Returns a blob for download.
 */
export async function exportProductVariantsCatalog(
  filters: Record<string, any>,
  request: ExportProductsRequest
) {
  return clientInstance.post('products/catalog/variants/export/', request, {
    params: filters,
    responseType: 'blob',
  })
}

// ============================================================================
// Variants Catalog API
// ============================================================================

/**
 * Fetch variants catalog with pagination and filtering.
 */
export async function getVariantsCatalog(
  filters: VariantsCatalogFilters = {},
  page: number = 1,
  page_size: number = 50,
  ordering?: string
) {
  return baseAPICall<VariantsCatalogResponse>(() =>
    clientInstance.get('products/catalog/variants/', {
      params: {
        ...filters,
        page,
        page_size,
        ordering,
      },
    })
  )
}

/**
 * Hook for fetching variants catalog with lazy loading.
 */
export interface UseVariantsCatalogOptions {
  filters?: VariantsCatalogFilters
  page?: number
  page_size?: number
  ordering?: string
  enabled?: boolean
}

export const useGetVariantsCatalog = (
  options: UseVariantsCatalogOptions = {}
) => {
  const {
    filters = {},
    page = 1,
    page_size = 50,
    ordering,
    enabled = true,
  } = options

  return useQuery({
    queryKey: [VARIANTS_CATALOG_KEY, filters, page, page_size, ordering],
    queryFn: () => getVariantsCatalog(filters, page, page_size, ordering),
    enabled,
    refetchOnMount: 'always',
  })
}

/**
 * Hook for fetching variants catalog with infinite scroll pagination.
 */
export const useGetVariantsCatalogInfinite = (
  filters: VariantsCatalogFilters = {},
  page_size: number = 50,
  enabled: boolean = true
) =>
  useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ['variants-catalog-infinite', filters, page_size],
    queryFn: ({ pageParam = 1 }) =>
      getVariantsCatalog(filters, pageParam, page_size),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.results.length < page_size) return undefined
      return allPages.length + 1
    },
    enabled,
  })

/**
 * Fetch single variant details by ID.
 */
export async function getVariantDetail(variantId: number) {
  return baseAPICall<VariantDetail>(() =>
    clientInstance.get(`products/catalog/variants/${variantId}/`)
  )
}

/**
 * Hook for fetching single variant details.
 */
export const useGetVariantDetail = (
  variantId: number,
  enabled: boolean = true
) =>
  useQuery({
    queryKey: ['variant-detail', variantId],
    queryFn: () => getVariantDetail(variantId),
    enabled: enabled && !!variantId,
  })

/**
 * Create a new variant.
 */
export async function createVariant(data: Partial<Variant>) {
  return baseAPICall<VariantDetail>(
    () => clientInstance.post('products/catalog/variants/', data),
    { successMessage: 'Variant created successfully' }
  )
}

/**
 * Hook for creating variants with automatic query invalidation.
 */
export const useCreateVariant = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createVariant,
    onSuccess: () => {
      invalidateProductQueries(queryClient)
    },
  })
}

/**
 * Update an existing variant.
 */
export async function updateVariant(variantId: number, data: Partial<Variant>) {
  return baseAPICall<VariantDetail>(
    () => clientInstance.put(`products/catalog/variants/${variantId}/`, data),
    { successMessage: 'Variant updated successfully' }
  )
}

/**
 * Hook for updating variants with automatic query invalidation.
 */
export const useUpdateVariant = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      variantId,
      data,
    }: {
      variantId: number
      data: Partial<Variant>
    }) => updateVariant(variantId, data),
    onSuccess: (_, variables) => {
      invalidateProductQueries(queryClient)
      // Also invalidate the specific variant detail
      queryClient.invalidateQueries({
        queryKey: ['variant-detail', variables.variantId],
      })
    },
  })
}

/**
 * Delete a variant.
 */
export async function deleteVariant(variantId: number) {
  return baseAPICall<void>(
    () => clientInstance.delete(`products/catalog/variants/${variantId}/`),
    { successMessage: 'Variant deleted successfully' }
  )
}

/**
 * Hook for deleting variants with automatic query invalidation.
 */
export const useDeleteVariant = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteVariant,
    onSuccess: () => {
      invalidateProductQueries(queryClient)
    },
  })
}

/**
 * Bulk match variants to a product.
 * Supports two modes:
 * 1. Explicit variant IDs: { matches: [{variant_id, product_id}] }
 * 2. Filter-based (select all): { product_id, filters, exclude_ids }
 *
 * Note: Success notification is handled by the component using this function
 * to show the actual matched count from the server response.
 */
export async function bulkMatchVariants(request: BulkMatchRequest) {
  return baseAPICall<BulkMatchResponse>(
    () => clientInstance.post('products/catalog/variants/bulk-match/', request)
    // No successMessage - component shows count-specific notification
  )
}

/**
 * Hook for bulk matching variants with optimistic updates.
 *
 * Implements optimistic UI:
 * - Updates cache immediately on mutation
 * - Rolls back on error
 * - Only invalidates queries for sync operations (async ones complete in background)
 */
export const useBulkMatchVariants = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: bulkMatchVariants,

    onMutate: async (request: BulkMatchRequest) => {
      // Cancel outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: [VARIANTS_CATALOG_KEY] })

      // Snapshot current data for rollback
      const previousData = queryClient.getQueriesData<VariantsCatalogResponse>({
        queryKey: [VARIANTS_CATALOG_KEY],
      })

      // Optimistically update the cache
      queryClient.setQueriesData<VariantsCatalogResponse>(
        { queryKey: [VARIANTS_CATALOG_KEY] },
        (old) => {
          if (!old) return old
          return {
            ...old,
            results: old.results.map((variant) => {
              // Handle explicit matches mode
              if (request.matches) {
                const match = request.matches.find(
                  (m) => m.variant_id === variant.id
                )
                if (match) {
                  return {
                    ...variant,
                    product_id: match.product_id,
                    product_name: match.product_id
                      ? request._productName || null
                      : null,
                    match_status: match.product_id ? 'matched' : 'unmatched',
                  } as VariantCatalog
                }
              }
              // Handle filter-based unlink mode
              if (
                request.unlink_mode &&
                !request.exclude_ids?.includes(variant.id)
              ) {
                return {
                  ...variant,
                  product_id: null,
                  product_name: null,
                  match_status: 'unmatched',
                } as VariantCatalog
              }
              // Handle filter-based link mode
              if (
                request.product_id &&
                !request.exclude_ids?.includes(variant.id)
              ) {
                return {
                  ...variant,
                  product_id: request.product_id,
                  product_name: request._productName || null,
                  match_status: 'matched',
                } as VariantCatalog
              }
              return variant
            }),
          }
        }
      )

      return { previousData }
    },

    onError: (_error, _request, context) => {
      // Rollback on error
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
    },

    onSettled: (data) => {
      // Only invalidate if NOT async (sync operations complete immediately)
      // For async operations, keep the optimistic state until user refreshes
      if (!data?.async) {
        invalidateProductQueries(queryClient)
      }
    },
  })
}

// ============================================================================
// Syndigo Integration API
// ============================================================================

export interface SyndigoSyncResponse {
  message: string
  sync_run_id?: number
  syndigo_product_id?: string
  status?: string
}

type SyndigoSyncStatus = 'completed' | 'in_progress' | 'failed' | 'pending'

export const getStatusColor = (status: SyndigoSyncStatus) => {
  switch (status) {
    case 'completed':
      return '#10b981' // green
    case 'in_progress':
      return '#f59e0b' // orange
    case 'failed':
      return '#ef4444' // red
    case 'pending':
    default:
      return '#6b7280' // gray
  }
}

export interface SyndigoSyncRunResponse {
  status: SyndigoSyncStatus
  lastSyncAt: string | null
  lastFullSyncAt: string | null
  lastIncrementalOrSingleSyncAt: string | null
}

/**
 * Trigger Syndigo sync for a specific variant.
 * Uses the variant's GTIN to fetch and sync data from Syndigo.
 */
export async function triggerSyndigoSync(variantId: number) {
  return baseAPICall<SyndigoSyncResponse>(
    () =>
      clientInstance.post(
        `products/catalog/variants/${variantId}/syndigo/sync/`
      ),
    { successMessage: 'Syndigo sync ran successfully' }
  )
}

/**
 * Hook for triggering Syndigo sync with automatic query invalidation.
 */
export const useTriggerSyndigoSync = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: triggerSyndigoSync,
    onSuccess: (_, variantId) => {
      invalidateProductQueries(queryClient)
      // Also invalidate the specific variant detail
      queryClient.invalidateQueries({
        queryKey: ['variant-detail', variantId],
      })
      // Invalidate sync status
      queryClient.invalidateQueries({
        queryKey: ['syndigo-sync-status'],
      })
    },
  })
}

/**
 * Get Syndigo sync status for the current company.
 * Returns status and timestamps of recent sync runs.
 */
export async function getSyndigoSyncStatus() {
  return baseAPICall<SyndigoSyncRunResponse>(() =>
    clientInstance.get('products/catalog/variants/syndigo/sync-status/')
  )
}

/**
 * Hook for fetching Syndigo sync status.
 * Always enabled.
 */
export const useGetSyndigoSyncStatus = (enabled: boolean = true) =>
  useQuery({
    queryKey: ['syndigo-sync-status'],
    queryFn: getSyndigoSyncStatus,
    enabled,
    refetchInterval: 10000, // Refetch every 10 seconds to track sync progress
  })

/**
 * Download Syndigo spec sheet PDF for a variant.
 * Uses authenticated request to fetch the PDF and returns a blob URL.
 *
 * @param variantId - The variant ID to fetch spec sheet for
 * @returns Blob URL that can be opened in a new window
 */
export async function downloadSyndigoSpecsheet(
  variantId: number
): Promise<string> {
  const response = await clientInstance.get(
    `/products/catalog/variants/${variantId}/syndigo/specsheet/`,
    {
      params: { output: 'pdf' },
      responseType: 'blob', // Handle binary data
    }
  )

  // Create a blob URL from the response
  const blob = new Blob([response.data], { type: 'application/pdf' })
  const blobUrl = URL.createObjectURL(blob)

  return blobUrl
}

/**
 * Create loading page DOM elements for the spec sheet loading state.
 */
function createLoadingPage(doc: Document): void {
  doc.title = 'Loading Spec Sheet...'

  // Create style element
  const style = doc.createElement('style')
  style.textContent = `
    body {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100vh;
      margin: 0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      background: #f5f5f5;
    }
    .loader {
      text-align: center;
    }
    .spinner {
      border: 4px solid #f3f3f3;
      border-top: 4px solid #067541;
      border-radius: 50%;
      width: 30px;
      height: 30px;
      animation: spin 1s linear infinite;
      margin: 0 auto 20px;
    }
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `
  doc.head.appendChild(style)

  // Create body content
  const loader = doc.createElement('div')
  loader.className = 'loader'

  const spinner = doc.createElement('div')
  spinner.className = 'spinner'

  const title = doc.createElement('h2')
  title.textContent = 'Generating spec sheet...'

  const description = doc.createElement('p')
  description.textContent = 'This may take a few moments.'

  loader.appendChild(spinner)
  loader.appendChild(title)
  loader.appendChild(description)
  doc.body.appendChild(loader)
}

/**
 * Create error page DOM elements for the spec sheet error state.
 */
function createErrorPage(doc: Document): void {
  doc.title = 'Error Loading Spec Sheet'

  // Create style element
  const style = doc.createElement('style')
  style.textContent = `
    body {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100vh;
      margin: 0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      background: #f5f5f5;
    }
    .error {
      text-align: center;
      color: #e74c3c;
    }
  `
  doc.head.appendChild(style)

  // Create body content
  const error = doc.createElement('div')
  error.className = 'error'

  const title = doc.createElement('h2')
  title.textContent = 'Error Loading Spec Sheet'

  const description = doc.createElement('p')
  description.textContent = 'Please try again later or contact support.'

  error.appendChild(title)
  error.appendChild(description)
  doc.body.appendChild(error)
}

/**
 * Open Syndigo spec sheet in a new tab with loading state.
 * Handles the entire flow: opening tab, showing loading spinner, fetching PDF, and error handling.
 *
 * @param variantId - The variant ID to fetch spec sheet for
 * @returns Promise that resolves when PDF starts loading, or rejects on error
 */
export async function openSyndigoSpecsheet(variantId: number): Promise<void> {
  // Open a new tab immediately to provide instant feedback
  const newTab = window.open('about:blank', '_blank')

  if (!newTab) {
    throw new Error('Please allow pop-ups to view the spec sheet.')
  }

  // Create loading page using modern DOM APIs
  createLoadingPage(newTab.document)

  try {
    // Fetch the PDF with authentication
    const blobUrl = await downloadSyndigoSpecsheet(variantId)

    // Navigate the opened tab to the PDF blob URL
    newTab.location.href = blobUrl

    // Clean up the blob URL after navigation
    setTimeout(() => {
      URL.revokeObjectURL(blobUrl)
    }, 1000)
  } catch (err) {
    console.error('Error opening spec sheet:', err)

    // Clear the loading page and show error using modern DOM APIs
    newTab.document.head.innerHTML = ''
    newTab.document.body.innerHTML = ''
    createErrorPage(newTab.document)

    // Re-throw to allow component to show toast notification
    throw err
  }
}

// ============================================================================
// Variant Images API
// ============================================================================

export interface VariantImage {
  id: number
  name: string
  original_name: string
  file: string | null
  mime_type: string
  size: number
  created: string
  modified: string
  created_by_name: string
  is_main: boolean
  /** Whether this image is managed by Syndigo (cannot be changed/deleted) */
  is_pim_asset: boolean
}

export interface VariantImagesResponse {
  count: number
  results: VariantImage[]
}

/**
 * Get all images for a variant with main image status.
 *
 * @param variantId - The variant ID
 */
export async function getVariantImages(variantId: number) {
  return baseAPICall<VariantImagesResponse>(() =>
    clientInstance.get(`products/catalog/variants/${variantId}/images/`)
  )
}

/**
 * Hook for fetching variant images.
 */
export const useGetVariantImages = (
  variantId: number | undefined,
  enabled: boolean = true
) =>
  useQuery({
    queryKey: ['variant-images', variantId],
    queryFn: () => getVariantImages(variantId!),
    enabled: enabled && !!variantId,
  })

// ============================================================================
// Variant Main Image API
// ============================================================================

export interface SetMainImageResponse {
  detail: string
  document_id: number
}

/**
 * Set a document as the main image for a variant's Syndigo product.
 * The main image is used on the spec sheet.
 *
 * @param variantId - The variant ID
 * @param documentId - The document ID to set as main image
 */
export async function setVariantMainImage(
  variantId: number,
  documentId: number
) {
  return baseAPICall<SetMainImageResponse>(
    () =>
      clientInstance.post(
        `products/catalog/variants/${variantId}/main-image/${documentId}/`
      ),
    { successMessage: 'Main image set successfully' }
  )
}

/**
 * Hook for setting variant main image with automatic query invalidation.
 */
export const useSetVariantMainImage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      variantId,
      documentId,
    }: {
      variantId: number
      documentId: number
    }) => setVariantMainImage(variantId, documentId),
    onSuccess: (_, variables) => {
      invalidateProductQueries(queryClient)
      // Invalidate the specific variant detail
      queryClient.invalidateQueries({
        queryKey: ['variant-detail', variables.variantId],
      })
      // Invalidate variant images query
      queryClient.invalidateQueries({
        queryKey: ['variant-images', variables.variantId],
      })
    },
  })
}

/**
 * Unset a document as the main image for a variant's Syndigo product.
 *
 * @param variantId - The variant ID
 * @param documentId - The document ID to unset as main image
 */
export async function unsetVariantMainImage(
  variantId: number,
  documentId: number
) {
  return baseAPICall<{ detail: string }>(
    () =>
      clientInstance.delete(
        `products/catalog/variants/${variantId}/main-image/${documentId}/`
      ),
    { successMessage: 'Main image unset successfully' }
  )
}

/**
 * Hook for unsetting variant main image with automatic query invalidation.
 */
export const useUnsetVariantMainImage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      variantId,
      documentId,
    }: {
      variantId: number
      documentId: number
    }) => unsetVariantMainImage(variantId, documentId),
    onSuccess: (_, variables) => {
      invalidateProductQueries(queryClient)
      // Invalidate the specific variant detail
      queryClient.invalidateQueries({
        queryKey: ['variant-detail', variables.variantId],
      })
      // Invalidate variant images query
      queryClient.invalidateQueries({
        queryKey: ['variant-images', variables.variantId],
      })
    },
  })
}

// ============================================================================
// View Mode Store
// ============================================================================

export type ProductsViewMode = 'products' | 'variants'

interface ProductsViewModeStore {
  viewMode: ProductsViewMode
  setViewMode: (mode: ProductsViewMode) => void
}

/**
 * Store for managing the products table view mode (products vs variants).
 * Persists selection to localStorage.
 *
 * @example
 * const { viewMode, setViewMode } = useProductsViewModeStore()
 * setViewMode('variants')
 */
export const useProductsViewModeStore = create<ProductsViewModeStore>()(
  persist(
    (set) => ({
      viewMode: 'products',
      setViewMode: (mode) => set({ viewMode: mode }),
    }),
    {
      name: 'products-view-mode-storage',
    }
  )
)

// ============================================================================
// Syndigo Filter Options API
// ============================================================================

export interface SyndigoFilterOption {
  label: string
  value: string
}

/**
 * Fetch distinct Syndigo categories for the company.
 * Returns array of {label, value} objects for filter dropdowns.
 */
export async function getSyndigoCategories() {
  return baseAPICall<SyndigoFilterOption[]>(() =>
    clientInstance.get('products/catalog/variants/syndigo/categories/')
  )
}

/**
 * Hook for fetching Syndigo categories.
 */
export const useGetSyndigoCategories = (enabled: boolean = true) =>
  useQuery({
    queryKey: ['syndigo-categories'],
    queryFn: getSyndigoCategories,
    enabled,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

/**
 * Fetch distinct Syndigo brands for the company.
 * Returns array of {label, value} objects for filter dropdowns.
 */
export async function getSyndigoBrands() {
  return baseAPICall<SyndigoFilterOption[]>(() =>
    clientInstance.get('products/catalog/variants/syndigo/brands/')
  )
}

/**
 * Hook for fetching Syndigo brands.
 */
export const useGetSyndigoBrands = (enabled: boolean = true) =>
  useQuery({
    queryKey: ['syndigo-brands'],
    queryFn: getSyndigoBrands,
    enabled,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

// ============================================================================
// Bulk Download Specsheets API
// ============================================================================

export interface BulkDownloadSpecsheetsRequest {
  include_ids: number[]
  exclude_ids?: number[]
}

export interface BulkDownloadResult {
  blob: Blob
  contentType: string
  filename: string
}

/** Response when starting an async bulk download (multiple variants) */
export interface AsyncBulkDownloadResponse {
  request_id: number
  status: 'processing' | 'completed' | 'failed' | 'expired'
  progress_percentage: number
  variant_count: number
}

/** Response when checking async bulk download status */
export interface BulkDownloadStatusResponse {
  request_id: number
  status: 'processing' | 'completed' | 'failed' | 'expired'
  progress_percentage: number
  variant_count: number
  download_url?: string
  error_message?: string
  detail?: string
  expires_at?: string
}

/**
 * Extract filename from Content-Disposition header.
 */
function extractFilenameFromHeader(
  contentDisposition: string | undefined
): string | null {
  if (!contentDisposition) return null
  const match = contentDisposition.match(/filename="?([^";\n]+)"?/)
  return match ? match[1] : null
}

/**
 * Check the status of an async bulk download request.
 *
 * @param requestId - The request ID returned from bulkDownloadSpecsheets
 * @returns BulkDownloadStatusResponse with status and download URL if completed
 */
export async function getBulkDownloadStatus(
  requestId: number
): Promise<BulkDownloadStatusResponse> {
  const response = await clientInstance.get(
    `/products/catalog/variants/syndigo/specsheets/bulk-download/${requestId}/status/`
  )
  return response.data
}

function getHeader(
  headers: Record<string, unknown>,
  name: string
): string | undefined {
  const lowerName = name.toLowerCase()
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === lowerName) {
      const value = headers[key]
      return typeof value === 'string' ? value : undefined
    }
  }
  return undefined
}

const CONTENT_TYPE_EXTENSIONS: Record<string, string> = {
  'text/csv': '.csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-excel': '.xls',
  'application/pdf': '.pdf',
  'application/zip': '.zip',
  'application/json': '.json',
  'text/plain': '.txt',
}

function buildFallbackFilename(contentType: string): string {
  const mimeType = contentType.split(';')[0].trim()
  const extension = CONTENT_TYPE_EXTENSIONS[mimeType] ?? '.zip'
  const date = new Date().toISOString().split('T')[0]
  return `download-${date}${extension}`
}

/**
 * Download a file from the given URL.
 *
 * @param downloadUrl - The URL to download the file from
 * @param signal - AbortSignal for cancellation
 * @param onProgress - Progress callback (0-100)
 * @param defaultFilename - Default filename to use if not provided by server (should be generated by caller)
 * @returns BulkDownloadResult containing the blob, content type, and filename
 */
export async function downloadFromUrl(
  downloadUrl: string,
  signal?: AbortSignal,
  onProgress?: (progress: number) => void,
  defaultFilename?: string
): Promise<BulkDownloadResult> {
  // Check if URL is absolute (S3 pre-signed URLs)
  const isAbsoluteUrl =
    downloadUrl.startsWith('http://') || downloadUrl.startsWith('https://')

  // Use plain axios for S3 URLs to avoid CORS issues from auth interceptors
  // Use clientInstance for relative URLs (our API endpoints)
  const axiosClient = isAbsoluteUrl ? axios : clientInstance

  const response = await axiosClient.get(downloadUrl, {
    // Only set baseURL for relative URLs
    ...(isAbsoluteUrl ? {} : { baseURL: API_HOST }),
    responseType: 'blob',
    signal,
    onDownloadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round(
          (progressEvent.loaded / progressEvent.total) * 100
        )
        onProgress(progress)
      }
    },
  })

  const contentType =
    getHeader(response.headers, 'content-type') ?? 'application/zip'
  const contentDisposition = getHeader(response.headers, 'content-disposition')

  // Determine filename from Content-Disposition header, or derive from content-type
  const headerFilename = extractFilenameFromHeader(contentDisposition)
  const filename =
    headerFilename || defaultFilename || buildFallbackFilename(contentType)

  return {
    blob: response.data,
    contentType,
    filename,
  }
}

/**
 * Bulk download spec sheets as PDF (single variant) or start async job (multiple variants).
 *
 * For single variant: Returns BulkDownloadResult with blob directly.
 * For multiple variants: Returns AsyncBulkDownloadResponse with request_id for polling.
 *
 * @param variantIds - Array of variant IDs to generate spec sheets for
 * @param signal - AbortSignal for cancellation
 * @param onProgress - Progress callback (0-100)
 * @returns BulkDownloadResult for single variant or AsyncBulkDownloadResponse for multiple
 */
export async function bulkDownloadSpecsheets(
  variantIds: number[],
  signal?: AbortSignal,
  onProgress?: (progress: number) => void
): Promise<BulkDownloadResult | AsyncBulkDownloadResponse> {
  const request: BulkDownloadSpecsheetsRequest = {
    include_ids: variantIds,
  }

  // For single variant, expect blob response
  // For multiple variants, expect JSON response with request_id
  const response = await clientInstance.post(
    '/products/catalog/variants/syndigo/specsheets/bulk-download/',
    request,
    {
      // Don't set responseType yet - we'll determine based on status code
      signal,
      onDownloadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded / progressEvent.total) * 100
          )
          onProgress(progress)
        }
      },
    }
  )

  // Check if it's an async response (202 Accepted)
  if (response.status === 202) {
    return response.data as AsyncBulkDownloadResponse
  }

  // For single variant, fetch again with blob responseType
  const blobResponse = await clientInstance.post(
    '/products/catalog/variants/syndigo/specsheets/bulk-download/',
    request,
    {
      responseType: 'blob',
      signal,
      onDownloadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded / progressEvent.total) * 100
          )
          onProgress(progress)
        }
      },
    }
  )

  const contentType = blobResponse.headers['content-type'] || 'application/zip'
  const contentDisposition = blobResponse.headers['content-disposition']
  const isPdf = contentType.includes('application/pdf')
  const defaultFilename = isPdf
    ? `specsheet_${new Date().toISOString().split('T')[0]}.pdf`
    : `specsheets_${new Date().toISOString().split('T')[0]}.zip`

  return {
    blob: blobResponse.data,
    contentType,
    filename: extractFilenameFromHeader(contentDisposition) || defaultFilename,
  }
}

/**
 * Bulk download spec sheets - optimized version that sets responseType based on variant count.
 *
 * @param variantIds - Array of variant IDs to generate spec sheets for
 * @param signal - AbortSignal for cancellation
 * @param onProgress - Progress callback (0-100)
 * @returns BulkDownloadResult for single variant or AsyncBulkDownloadResponse for multiple
 */
export async function bulkDownloadSpecsheetsOptimized(
  variantIds: number[],
  signal?: AbortSignal,
  onProgress?: (progress: number) => void
): Promise<BulkDownloadResult | AsyncBulkDownloadResponse> {
  const request: BulkDownloadSpecsheetsRequest = {
    include_ids: variantIds,
  }

  // Single variant - expect blob response
  if (variantIds.length === 1) {
    const response = await clientInstance.post(
      '/products/catalog/variants/syndigo/specsheets/bulk-download/',
      request,
      {
        responseType: 'blob',
        signal,
        onDownloadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            )
            onProgress(progress)
          }
        },
      }
    )

    const contentType = response.headers['content-type'] || 'application/pdf'
    const contentDisposition = response.headers['content-disposition']
    const defaultFilename = `specsheet_${new Date().toISOString().split('T')[0]}.pdf`

    return {
      blob: response.data,
      contentType,
      filename:
        extractFilenameFromHeader(contentDisposition) || defaultFilename,
    }
  }

  // Multiple variants - expect JSON response with request_id
  const response = await clientInstance.post(
    '/products/catalog/variants/syndigo/specsheets/bulk-download/',
    request,
    { signal }
  )

  return response.data as AsyncBulkDownloadResponse
}

/**
 * Bulk download spec sheets from deals as PDF (single variant) or async job (multiple variants).
 * Gets variants associated with selected deals and generates spec sheets for them.
 *
 * @param dealIds - Array of deal IDs to get spec sheets for
 * @param signal - AbortSignal for cancellation
 * @param onProgress - Progress callback (0-100)
 * @returns BulkDownloadResult for single variant or AsyncBulkDownloadResponse for multiple
 */
export async function bulkDownloadSpecsheetsFromDeals(
  dealIds: number[],
  signal?: AbortSignal,
  onProgress?: (progress: number) => void
): Promise<BulkDownloadResult | AsyncBulkDownloadResponse> {
  const request: BulkDownloadSpecsheetsRequest = {
    include_ids: dealIds,
  }

  // Use arraybuffer to handle both blob and JSON responses
  const response = await clientInstance.post(
    '/contacts/deals/bulk/specsheet-download/',
    request,
    {
      responseType: 'arraybuffer',
      signal,
      validateStatus: (status) => status === 200 || status === 202,
      onDownloadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded / progressEvent.total) * 100
          )
          onProgress(progress)
        }
      },
    }
  )

  const contentType = response.headers['content-type'] || ''

  // 202 Accepted or JSON content = async response with request_id
  if (response.status === 202 || contentType.includes('application/json')) {
    // Parse JSON from arraybuffer
    const decoder = new TextDecoder('utf-8')
    const jsonStr = decoder.decode(response.data)
    return JSON.parse(jsonStr) as AsyncBulkDownloadResponse
  }

  // 200 OK with PDF/binary = blob response (single variant)
  const contentDisposition = response.headers['content-disposition']
  const defaultFilename = `deal_specsheet_${new Date().toISOString().split('T')[0]}.pdf`

  return {
    blob: new Blob([response.data], { type: contentType }),
    contentType,
    filename: extractFilenameFromHeader(contentDisposition) || defaultFilename,
  }
}

/**
 * Bulk download spec sheets from deal reports as PDF (single variant) or async job (multiple variants).
 * Gets variants associated with selected deal reports and generates spec sheets for them.
 *
 * @param dealIds - Array of deal report IDs to get spec sheets for
 * @param signal - AbortSignal for cancellation
 * @param onProgress - Progress callback (0-100)
 * @returns BulkDownloadResult for single variant or AsyncBulkDownloadResponse for multiple
 */
export async function bulkDownloadSpecsheetsFromDealReports(
  dealIds: number[],
  signal?: AbortSignal,
  onProgress?: (progress: number) => void
): Promise<BulkDownloadResult | AsyncBulkDownloadResponse> {
  const request: BulkDownloadSpecsheetsRequest = {
    include_ids: dealIds,
  }

  // Use arraybuffer to handle both blob and JSON responses
  const response = await clientInstance.post(
    '/reports/deals/bulk/specsheet-download/',
    request,
    {
      responseType: 'arraybuffer',
      signal,
      validateStatus: (status) => status === 200 || status === 202,
      onDownloadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded / progressEvent.total) * 100
          )
          onProgress(progress)
        }
      },
    }
  )

  const contentType = response.headers['content-type'] || ''

  // 202 Accepted or JSON content = async response with request_id
  if (response.status === 202 || contentType.includes('application/json')) {
    // Parse JSON from arraybuffer
    const decoder = new TextDecoder('utf-8')
    const jsonStr = decoder.decode(response.data)
    return JSON.parse(jsonStr) as AsyncBulkDownloadResponse
  }

  // 200 OK with PDF/binary = blob response (single variant)
  const contentDisposition = response.headers['content-disposition']
  const defaultFilename = `deal_specsheet_${new Date().toISOString().split('T')[0]}.pdf`

  return {
    blob: new Blob([response.data], { type: contentType }),
    contentType,
    filename: extractFilenameFromHeader(contentDisposition) || defaultFilename,
  }
}
